/* product-image-picklist@1: shared progressive enhancement of native selects. */
(() => {
  'use strict';
  if (typeof window === 'undefined' || customElements.get('guru-image-picklist')) return;
  let sequence = 0;
  const imageUrl = (raw) => {
    try { const url = new URL(raw, location.href); return raw && ['https:', 'http:'].includes(url.protocol) ? url.href : ''; }
    catch { return ''; }
  };
  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    node.className = className;
    if (text != null) node.textContent = text;
    return node;
  };

  class GuruImagePicklist extends HTMLElement {
    connectedCallback() {
      if (this.controller || !('showPopover' in HTMLElement.prototype)) return;
      const select = this.querySelector('select');
      if (!select) return;
      this.select = select;
      this.showImages = this.dataset.picklistMedia !== 'none';
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.originalTabindex = select.getAttribute('tabindex');
      this.originalAriaHidden = select.getAttribute('aria-hidden');
      this.label = [...select.labels || []].map(n => n.textContent.trim()).join(' ') || select.getAttribute('aria-label') || '';
      this.more = this.querySelector('.variant-picker__more')?.textContent.trim() || '';
      this.trigger = element('button', 'guru-image-picklist__trigger');
      this.trigger.type = 'button';
      this.trigger.setAttribute('aria-haspopup', 'listbox');
      this.trigger.setAttribute('aria-expanded', 'false');
      this.menu = element('div', 'guru-image-picklist__menu');
      this.menu.id = `guru-image-picklist-${++sequence}`;
      this.menu.setAttribute('popover', 'manual');
      this.menu.setAttribute('role', 'listbox');
      this.list = element('div', 'guru-image-picklist__list');
      this.scrollThumb = element('span', 'guru-image-picklist__scroll-thumb');
      this.scrollThumb.setAttribute('aria-hidden', 'true');
      this.menu.append(this.list, this.scrollThumb);
      this.menu.setAttribute('aria-label', this.label);
      this.trigger.setAttribute('aria-controls', this.menu.id);
      select.tabIndex = -1;
      select.setAttribute('aria-hidden', 'true');
      this.append(this.trigger, this.menu);
      this.dataset.enhanced = 'true';
      this.trigger.addEventListener('click', () => this.open ? this.close(true) : this.show(), { signal });
      this.addEventListener('keydown', event => this.onKey(event), { signal, capture: true });
      this.list.addEventListener('scroll', () => this.updateScrollThumb(), { signal, passive: true });
      this.scrollThumb.addEventListener('pointerdown', event => {
        event.preventDefault();
        this.dragStart = { y: event.clientY, scroll: this.list.scrollTop };
        this.scrollThumb.setPointerCapture(event.pointerId);
      }, { signal });
      this.scrollThumb.addEventListener('pointermove', event => {
        if (!this.dragStart) return;
        const travel = this.list.clientHeight - this.scrollThumb.offsetHeight;
        if (travel > 0) this.list.scrollTop = this.dragStart.scroll + (event.clientY - this.dragStart.y) * (this.list.scrollHeight - this.list.clientHeight) / travel;
      }, { signal });
      this.scrollThumb.addEventListener('lostpointercapture', () => { this.dragStart = null; }, { signal });
      this.menu.addEventListener('click', event => {
        const row = event.target.closest('[role="option"]');
        if (row) this.choose(row.dataset.value);
      }, { signal });
      select.addEventListener('change', () => this.refresh(), { signal });
      document.addEventListener('pointerdown', event => {
        if (this.open && !event.composedPath().includes(this)) this.close(false);
      }, { signal, capture: true });
      window.addEventListener('resize', () => this.position(), { signal });
      window.addEventListener('scroll', event => {
        if (!this.menu.contains(event.target)) this.position();
      }, { signal, capture: true });
      window.visualViewport?.addEventListener('resize', () => this.position(), { signal });
      this.observer = new MutationObserver(records => {
        if (records.some(record => record.target === select || select.contains(record.target) || (record.target === this && record.attributeName === 'data-selected-value'))) this.refresh();
      });
      this.observer.observe(this, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['disabled', 'selected', 'data-image', 'data-selected-value'] });
      this.refresh();
    }

    disconnectedCallback() {
      this.close(false);
      this.controller?.abort();
      this.observer?.disconnect();
      this.trigger?.remove();
      this.menu?.remove();
      if (this.select) {
        for (const [name, value] of [['tabindex', this.originalTabindex], ['aria-hidden', this.originalAriaHidden]]) {
          if (value == null) this.select.removeAttribute(name); else this.select.setAttribute(name, value);
        }
      }
      delete this.dataset.enhanced;
      this.controller = null;
      this.dragStart = null;
    }

    thumbnail(option) {
      const frame = element('span', 'guru-image-picklist__image');
      frame.setAttribute('aria-hidden', 'true');
      const url = imageUrl(option?.dataset.image);
      if (url) {
        const image = element('img', '');
        image.src = url; image.alt = ''; image.width = 40; image.height = 40;
        image.loading = 'lazy'; image.decoding = 'async';
        image.addEventListener('error', () => image.remove(), { once: true });
        frame.append(image);
      }
      return frame;
    }

    refresh() {
      const option = this.select.selectedOptions[0];
      const label = option?.textContent.trim() || '';
      this.trigger.disabled = this.select.disabled;
      this.trigger.setAttribute('aria-label', `${this.label}: ${label}`);
      this.trigger.replaceChildren(element('span', 'guru-image-picklist__text', label));
      if (this.showImages) this.trigger.prepend(this.thumbnail(option));
      if (this.more) this.trigger.append(element('span', 'guru-image-picklist__more', this.more));
      const chevron = element('span', 'guru-image-picklist__chevron');
      chevron.setAttribute('aria-hidden', 'true');
      this.trigger.append(chevron);
      if (this.trigger.disabled) this.close(false);
      else if (this.open) {
        const focused = this.menu.contains(document.activeElement) ? document.activeElement.dataset.value : null;
        this.renderOptions(); this.position();
        if (focused != null) (this.rows().find(row => row.dataset.value === focused) || this.rows()[0])?.focus({ preventScroll: true });
      }
    }

    renderOptions() {
      this.list.replaceChildren(...[...this.select.options].filter(option => !option.hidden).map(option => {
        const row = element('button', 'guru-image-picklist__option');
        row.type = 'button'; row.tabIndex = -1; row.dataset.value = option.value;
        row.setAttribute('role', 'option'); row.setAttribute('aria-selected', String(option.selected));
        row.disabled = option.disabled || !!option.closest('optgroup[disabled]');
        row.setAttribute('aria-disabled', String(row.disabled));
        row.append(element('span', 'guru-image-picklist__text', option.textContent.trim()));
        if (this.showImages) row.prepend(this.thumbnail(option));
        const check = element('span', 'guru-image-picklist__check', option.selected ? '✓' : '');
        check.setAttribute('aria-hidden', 'true'); row.append(check);
        return row;
      }));
    }

    rows() { return [...this.menu.querySelectorAll('[role="option"]:not(:disabled)')]; }

    show() {
      if (this.select.disabled || !this.isConnected) return;
      this.renderOptions();
      this.menu.showPopover(); this.open = true;
      this.trigger.setAttribute('aria-expanded', 'true');
      this.position();
      const selected = this.rows().find(row => row.getAttribute('aria-selected') === 'true') || this.rows()[0];
      selected?.focus({ preventScroll: true });
      if (selected) this.revealRow(selected);
    }

    close(restoreFocus) {
      if (!this.open) return;
      this.open = false;
      if (this.menu.matches(':popover-open')) this.menu.hidePopover();
      this.trigger.setAttribute('aria-expanded', 'false');
      if (restoreFocus && this.trigger.isConnected) this.trigger.focus({ preventScroll: true });
    }

    choose(value) {
      const option = [...this.select.options].find(option => option.value === value);
      if (!option || option.disabled || option.closest('optgroup[disabled]') || this.select.disabled) return;
      this.select.value = value;
      this.close(true);
      this.select.dispatchEvent(new Event('change', { bubbles: true }));
      this.refresh();
    }

    position() {
      if (!this.open) return;
      const rect = this.trigger.getBoundingClientRect();
      const viewport = window.visualViewport;
      const left = viewport?.offsetLeft || 0, top = viewport?.offsetTop || 0;
      const width = viewport?.width || document.documentElement.clientWidth;
      const height = viewport?.height || window.innerHeight;
      const below = top + height - rect.bottom - 18, above = rect.top - top - 18;
      const upwards = below < 200 && above > below;
      this.menu.style.width = `${Math.min(rect.width, width - 24)}px`;
      const maxHeight = Math.max(62, Math.min(320, upwards ? above : below));
      this.menu.style.maxHeight = `${maxHeight}px`;
      this.list.style.maxHeight = `${maxHeight - 14}px`;
      this.menu.style.left = `${Math.max(left + 12, Math.min(rect.left, left + width - this.menu.offsetWidth - 12))}px`;
      this.menu.style.top = `${upwards ? Math.max(top + 12, rect.top - this.menu.offsetHeight - 6) : rect.bottom + 6}px`;
      this.updateScrollThumb();
    }

    updateScrollThumb() {
      const overflow = this.list.scrollHeight - this.list.clientHeight;
      this.scrollThumb.hidden = overflow <= 1;
      if (overflow <= 1) return;
      const height = Math.max(24, this.list.clientHeight ** 2 / this.list.scrollHeight);
      this.scrollThumb.style.height = `${height}px`;
      this.scrollThumb.style.top = `${6 + this.list.scrollTop / overflow * (this.list.clientHeight - height)}px`;
    }

    revealRow(row) {
      const box = this.list.getBoundingClientRect(), target = row.getBoundingClientRect();
      if (target.top < box.top) this.list.scrollTop -= box.top - target.top;
      else if (target.bottom > box.bottom) this.list.scrollTop += target.bottom - box.bottom;
      this.updateScrollThumb();
    }

    onKey(event) {
      if (!this.open) {
        if (event.target === this.trigger && ['ArrowDown', 'ArrowUp'].includes(event.key)) { event.preventDefault(); this.show(); }
        return;
      }
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); this.close(true); return; }
      if (event.key === 'Tab') { this.close(true); return; }
      const rows = this.rows();
      if (!rows.length) return;
      const index = rows.indexOf(document.activeElement);
      let next;
      if (event.key === 'ArrowDown') next = rows[(index + 1) % rows.length];
      if (event.key === 'ArrowUp') next = rows[(index - 1 + rows.length) % rows.length];
      if (event.key === 'Home') next = rows[0];
      if (event.key === 'End') next = rows.at(-1);
      if (['Enter', ' '].includes(event.key) && index >= 0) { event.preventDefault(); this.choose(rows[index].dataset.value); return; }
      if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey && event.key !== ' ') {
        const now = Date.now(); this.search = now - (this.searchAt || 0) < 700 ? (this.search || '') + event.key : event.key; this.searchAt = now;
        next = [...rows.slice(index + 1), ...rows.slice(0, index + 1)].find(row => row.textContent.trim().toLocaleLowerCase().startsWith(this.search.toLocaleLowerCase()));
      }
      if (next) { event.preventDefault(); next.focus({ preventScroll: true }); this.revealRow(next); }
    }
  }
  customElements.define('guru-image-picklist', GuruImagePicklist);
})();
